import { useState, useEffect, useRef, useId } from 'react';
import Icon from "../icons/Icon";
import Modal from '../modals/Modal';
import { useAuth } from '../../hooks/useAuth';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useToast } from '../../contexts/ToastContext';
import useDepartments from '../../hooks/useDepartments';
import '../../styles/components/departments-page.css';

// Deterministic color palette for department dots
const DEPT_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4',
  '#F97316', '#14B8A6', '#6366F1', '#E11D48', '#84CC16', '#A855F7',
];

const DepartmentsPage = () => {
  const { company, hasPermission } = useAuth();
  const canEdit = hasPermission('set:dept:edit');
  const toast = useToast();
  const { items, loading, saveDepartments } = useDepartments();

  // Inline add/edit state
  const [mode, setMode] = useState(null); // null | 'add' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const [saving, setSaving] = useState(false);
  const deleteTitleId = useId();
  const inputRef = useRef(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Employee counts per department
  const [employeeCounts, setEmployeeCounts] = useState({});

  // Fetch employee counts (only if the caller can read the employees collection).
  // Departments View doesn't require emp:view, so a Settings-only role can
  // land here without it. Skip the fetch rather than firing a permission-denied
  // request — counts will display as "0" via the falsy default, which is
  // acceptable for a viewer who can't see the underlying employees anyway.
  useEffect(() => {
    if (!company) return;
    if (!hasPermission('emp:view')) return;
    const fetchCounts = async () => {
      try {
        const q = query(
          collection(db, 'companies', company.id, 'employees'),
          where('role', '==', 'employee')
        );
        const snap = await getDocs(q);
        const counts = {};
        snap.docs.forEach((doc) => {
          const dept = doc.data().employment?.department;
          if (dept) {
            counts[dept] = (counts[dept] || 0) + 1;
          }
        });
        setEmployeeCounts(counts);
      } catch (err) {
        console.error('[DepartmentsPage] employee count error:', err);
      }
    };
    fetchCounts();
  }, [company, hasPermission]);

  // Focus input when mode changes
  useEffect(() => {
    if (mode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mode]);

  // ─── Validation ───

  const validate = (name) => {
    const trimmed = name.trim();
    if (!trimmed) {
      return 'Department name is required';
    }
    if (trimmed.length > 30) {
      return 'Department name must be 30 characters or less';
    }
    if (!/^[a-zA-Z0-9 ]+$/.test(trimmed)) {
      return 'Department name can only contain letters, numbers, and spaces';
    }
    const isDuplicate = items.some(
      (d) => d.name.toLowerCase() === trimmed.toLowerCase() && d.id !== editingId
    );
    if (isDuplicate) {
      return 'A department with this name already exists';
    }
    return '';
  };

  // ─── Add ───

  const openAdd = () => {
    setMode('add');
    setEditingId(null);
    setInputValue('');
    setInputError('');
  };

  // ─── Edit ───

  const openEdit = (dept) => {
    setMode('edit');
    setEditingId(dept.id);
    setInputValue(dept.name);
    setInputError('');
  };

  // ─── Cancel ───

  const cancelInline = () => {
    setMode(null);
    setEditingId(null);
    setInputValue('');
    setInputError('');
  };

  // ─── Save (add or edit) ───

  const handleSave = async () => {
    const trimmed = inputValue.trim();
    const error = validate(trimmed);
    if (error) {
      setInputError(error);
      toast.error(error);
      return;
    }

    setSaving(true);
    const now = new Date().toISOString();

    try {
      let updated;
      if (mode === 'add') {
        const newDept = {
          id: `dept_${Date.now()}`,
          name: trimmed,
          createdAt: now,
          updatedAt: now,
        };
        updated = [...items, newDept];
      } else {
        updated = items.map((d) =>
          d.id === editingId ? { ...d, name: trimmed, updatedAt: now } : d
        );
      }
      await saveDepartments(updated);
      toast.success(mode === 'add' ? `"${trimmed}" added` : `Department renamed to "${trimmed}"`);
      cancelInline();
    } catch {
      toast.error('Failed to save department');
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete ───

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const updated = items.filter((d) => d.id !== deleteTarget.id);
      await saveDepartments(updated);
      toast.success(`"${deleteTarget.name}" deleted`);
    } catch {
      toast.error('Failed to delete department');
    } finally {
      setSaving(false);
      setDeleteTarget(null);
    }
  };

  // ─── Key handler for inline input ───

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      cancelInline();
    }
  };

  // ─── Render: Loading ───

  if (loading) {
    return (
      <div>
        <h1 className="dept-page-title">Departments</h1>
        <p className="dept-page-subtitle">Manage company departments for employee assignment</p>
        <div className="dept-loading">
          <div className="dept-loading__spinner"></div>
          Loading departments...
        </div>
      </div>
    );
  }

  // ─── Render ───

  return (
    <div>
      <h1 className="dept-page-title">Departments</h1>
      <p className="dept-page-subtitle">Manage company departments for employee assignment</p>

      <div className="dept-card">
        <div className="dept-card-header">
          <div className="dept-card-header-left">
            <div className="dept-card-icon">
              <Icon name="grid" size={20} />
            </div>
            <div>
              <div className="dept-card-title">Company Departments</div>
              <div className="dept-card-description">
                {items.length} department{items.length !== 1 ? 's' : ''} configured
              </div>
            </div>
          </div>
          {canEdit && (
            <button
              className="dept-btn-add"
              onClick={openAdd}
              disabled={mode === 'add'}
            >
              <Icon name="plus" size={14} />
              Add Department
            </button>
          )}
        </div>

        <div className="dept-card-body">
          {/* Inline add row */}
          {canEdit && (
            <>
              <div className={`dept-inline-row ${mode === 'add' ? 'active' : ''}`}>
                <div className="dept-color" style={{ background: DEPT_COLORS[items.length % DEPT_COLORS.length] }}></div>
                <input
                  ref={mode === 'add' ? inputRef : null}
                  type="text"
                  className={`dept-inline-input ${inputError ? 'error' : ''}`}
                  value={mode === 'add' ? inputValue : ''}
                  onChange={(e) => { setInputValue(e.target.value); setInputError(''); }}
                  onKeyDown={handleKeyDown}
                  placeholder="Department name"
                  maxLength={30}
                />
                <span className="dept-inline-hint">Max 30 chars</span>
                <div className="dept-inline-actions">
                  <button className="dept-inline-btn save" title="Save" onClick={handleSave} disabled={saving}>
                    <Icon name="check" size={16} />
                  </button>
                  <button className="dept-inline-btn cancel" title="Cancel" onClick={cancelInline}>
                    <Icon name="x" size={16} />
                  </button>
                </div>
              </div>
              {mode === 'add' && inputError && (
                <div className="dept-inline-error active">{inputError}</div>
              )}
            </>
          )}

          {/* Department list */}
          {items.length === 0 && mode !== 'add' ? (
            <div className="dept-empty-state">
              <div className="dept-empty-state__icon"><Icon name="building-2" size={32} /></div>
              <div className="dept-empty-state__title">No departments yet</div>
              <div className="dept-empty-state__text">
                {canEdit ? 'Add your first department to get started.' : 'No departments have been configured.'}
              </div>
              {canEdit && (
                <button className="dept-btn-add" onClick={openAdd}>
                  Add Department
                </button>
              )}
            </div>
          ) : (
            <ul className="dept-list">
              {items.map((dept, index) => (
                <li key={dept.id} className="dept-item">
                  {canEdit && mode === 'edit' && editingId === dept.id ? (
                    <>
                      <div className="dept-color" style={{ background: DEPT_COLORS[index % DEPT_COLORS.length] }}></div>
                      <input
                        ref={inputRef}
                        type="text"
                        className={`dept-inline-input ${inputError ? 'error' : ''}`}
                        value={inputValue}
                        onChange={(e) => { setInputValue(e.target.value); setInputError(''); }}
                        onKeyDown={handleKeyDown}
                        placeholder="Department name"
                        maxLength={30}
                      />
                      <span className="dept-inline-hint">Max 30 chars</span>
                      <div className="dept-inline-actions">
                        <button className="dept-inline-btn save" title="Save" onClick={handleSave} disabled={saving}>
                          <Icon name="check" size={16} />
                        </button>
                        <button className="dept-inline-btn cancel" title="Cancel" onClick={cancelInline}>
                          <Icon name="x" size={16} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="dept-item-left">
                        <div className="dept-color" style={{ background: DEPT_COLORS[index % DEPT_COLORS.length] }}></div>
                        <span className="dept-name">{dept.name}</span>
                        <span className="dept-count">
                          {employeeCounts[dept.name] || 0} employee{(employeeCounts[dept.name] || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {canEdit && (
                        <div className="dept-actions">
                          <button
                            className="dept-action-btn"
                            title="Edit"
                            onClick={() => openEdit(dept)}
                            disabled={mode !== null}
                          >
                            <Icon name="pencil" size={14} />
                          </button>
                          <button
                            className="dept-action-btn delete"
                            title="Delete"
                            onClick={() => setDeleteTarget(dept)}
                            disabled={mode !== null}
                          >
                            <Icon name="trash-2" size={14} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canEdit && mode === 'edit' && inputError && (
            <div className="dept-inline-error active">{inputError}</div>
          )}
        </div>
      </div>

      {/* Info box */}
      <div className="dept-info-box">
        <span className="dept-info-box__icon">
          <Icon name="info" size={18} />
        </span>
        <span className="dept-info-box__text">
          Departments are used when adding or editing employees. Deleting a department will <strong>not</strong> remove it from existing employee records.
        </span>
      </div>

      {/* Delete Confirmation Modal */}
      {canEdit && deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)} busy={saving} labelledBy={deleteTitleId} className="dept-delete-modal">
            <div className="dept-delete-modal__title" id={deleteTitleId}>Delete Department</div>
            <div className="dept-delete-modal__text">
              Are you sure you want to delete "{deleteTarget.name}"?
              {(employeeCounts[deleteTarget.name] || 0) > 0 && (
                <> This department currently has {employeeCounts[deleteTarget.name]} employee{employeeCounts[deleteTarget.name] !== 1 ? 's' : ''} assigned. Their records will not be affected.</>
              )}
            </div>
            <div className="dept-delete-modal__actions">
              <button className="dept-btn-cancel" onClick={() => setDeleteTarget(null)} disabled={saving}>Cancel</button>
              <button className="dept-btn-danger" onClick={handleDeleteConfirm} disabled={saving}>
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default DepartmentsPage;
